import { z } from 'zod';

import { createMappers } from '@/lib/mapping';
import {
  CheckInObjectiveResponse,
  CheckInObjectiveType,
  ProviderTreatmentStatus,
  TreatmentCheckInObjective,
  TreatmentCheckInProviderUpdate,
  TreatmentFrequency,
  TreatmentFrequencyInterval,
} from '@/services/types/treatment-types';

const providerUpdateSchema = z.object({
  updateId: z.string(),
  checkInId: z.string().optional().nullable(),
  createdAt: z.string().optional().nullable(),
  modifiedAt: z.string().optional().nullable(),
  providerId: z.string().min(1),
  newProviderName: z.string().min(1),
  providerSpecialty: z.string().nullish(),
  newTreatmentStatus: z.nativeEnum(ProviderTreatmentStatus).nullable(),
  newTreatmentNotes: z.string().nullish(),
  newTreatmentStatusOccurredAt: z.string().nullish(),
  newTreatmentFrequency: z.nativeEnum(TreatmentFrequency).nullish(),
  newTreatmentFrequencyTimes: z.number().min(1).max(14).nullish(),
  newTreatmentFrequencyInterval: z
    .nativeEnum(TreatmentFrequencyInterval)
    .nullish(),
  newTreatmentFrequencyText: z.string().nullish(),
  newFirstTreatmentDate: z.string().nullish(),
  isFirstAppointment: z.boolean().default(false),
});

const objectiveSchema = z.object({
  id: z.string(),
  objectiveType: z.nativeEnum(CheckInObjectiveType),
  label: z.string(),
  providerId: z.string().nullable(),
  providerName: z.string().nullable(),
  response: z.nativeEnum(CheckInObjectiveResponse),
  responseNote: z.string().nullish(),
  sortOrder: z.number(),
  source: z.string(),
});

export const checkInFormSchema = z.object({
  general_notes: z.string().nullish(),
  pain_level: z.number().min(-1).max(10).nullable(),
  pain_level_note: z.string().nullish(),
  provider_updates: z.array(providerUpdateSchema),
  objectives: z.array(objectiveSchema),
});

export type FormProviderUpdate = z.infer<typeof providerUpdateSchema>;
export type FormObjective = z.infer<typeof objectiveSchema>;
export type EditCheckInFormValues = z.infer<typeof checkInFormSchema>;

export const [
  apiProviderUpdateToFormProviderUpdate,
  formProviderUpdateToApiProviderUpdate,
] = createMappers<TreatmentCheckInProviderUpdate, FormProviderUpdate>({
  id: 'updateId',
  created_at: 'createdAt',
  modified_at: 'modifiedAt',
  check_in_id: 'checkInId',
  provider_id: 'providerId',
  provider_name: 'newProviderName',
  provider_specialty: 'providerSpecialty',
  new_treatment_status: 'newTreatmentStatus',
  new_treatment_notes: 'newTreatmentNotes',
  new_treatment_status_occurred_at: 'newTreatmentStatusOccurredAt',
  new_treatment_frequency: 'newTreatmentFrequency',
  new_treatment_frequency_times: 'newTreatmentFrequencyTimes',
  new_treatment_frequency_interval: 'newTreatmentFrequencyInterval',
  new_treatment_frequency_text: 'newTreatmentFrequencyText',
  new_first_treatment_date: 'newFirstTreatmentDate',
  is_first_appointment: 'isFirstAppointment',
  // Deprecated fields - map to unique dummy keys to avoid collision
  new_treatment_progress: '__deprecated_progress' as any,
  new_pain_level: '__deprecated_pain_level' as any,
});

export const apiObjectiveToFormObjective = (
  obj: TreatmentCheckInObjective,
): FormObjective => ({
  id: obj.id,
  objectiveType: obj.objective_type,
  label: obj.label,
  providerId: obj.provider_id,
  providerName: obj.provider_name,
  response: obj.response,
  responseNote: obj.response_note,
  sortOrder: obj.sort_order,
  source: obj.source,
});

export const formObjectiveToApiObjective = (
  obj: FormObjective,
): { id: string; response: string; response_note?: string | null } => ({
  id: obj.id,
  response: obj.response,
  response_note:
    obj.response === CheckInObjectiveResponse.COULD_NOT_COLLECT
      ? obj.responseNote ?? null
      : null,
});

/**
 * Merge server objectives into the current form objectives.
 * New objectives (by id) are appended; existing form entries are kept as-is
 * so user-entered responses/notes are not clobbered.
 */
export const mergeServerObjectives = (
  currentForm: FormObjective[],
  serverObjectives: TreatmentCheckInObjective[],
): FormObjective[] => {
  const existingIds = new Set(currentForm.map((o) => o.id));
  const newEntries = serverObjectives
    .filter((o) => !existingIds.has(o.id))
    .map(apiObjectiveToFormObjective);

  if (newEntries.length === 0) return currentForm;
  return [...currentForm, ...newEntries];
};
